import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, SignOut, Link as LinkIcon, Copy } from "@phosphor-icons/react";
import { API, getToken, clearToken, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";
import AgendaTab from "@/components/panel/AgendaTab";
import ServicesTab from "@/components/panel/ServicesTab";
import ProfileTab from "@/components/panel/ProfileTab";
import BillingTab from "@/components/panel/BillingTab";
import ToolsTab from "@/components/panel/ToolsTab";

export default function BarberPanel() {
  const navigate = useNavigate();
  const { t, setLocale } = useLocale();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("agenda");

  const TABS = [
    { key: "agenda", label: t("tab_agenda") },
    { key: "servicos", label: t("tab_services") },
    { key: "perfil", label: t("tab_profile") },
    { key: "assinatura", label: t("tab_billing") },
    { key: "ferramentas", label: t("tab_tools") },
  ];

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/barber/me`, { headers: authHeaders() });
      setData(res.data);
      setLocale(res.data.barber.country === "py" ? "es" : "pt");
    } catch (e) {
      if (e.response?.status === 401) {
        clearToken();
        navigate("/entrar");
      } else if (e.response?.status === 403) {
        try {
          const me = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
          navigate(me.data.role === "partner" ? "/socio" : "/admin");
        } catch {
          clearToken();
          navigate("/entrar");
        }
      } else {
        toast.error(t("panel_error"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) return navigate("/entrar");
    load();
  }, [load, navigate]);

  const logout = () => {
    clearToken();
    navigate("/entrar");
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="panel-loading">
        <div className="h-10 w-10 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/b/${data.barber.slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t("link_copied"));
    } catch {
      toast.info(publicUrl);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto px-4 py-6 pb-16" data-testid="barber-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-500">
            <Scissors size={18} weight="duotone" />
            <span className="font-display font-bold">{data.barber.shop_name}</span>
          </div>
          <button
            data-testid="panel-logout-btn"
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-400 transition-colors"
          >
            <SignOut size={16} weight="bold" /> {t("logout")}
          </button>
        </div>

        <button
          data-testid="copy-public-link-btn"
          onClick={copyLink}
          className="mt-4 w-full flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-left hover:border-amber-500/60 transition-colors"
        >
          <LinkIcon size={16} className="text-amber-500 shrink-0" weight="duotone" />
          <span className="text-xs text-zinc-400 truncate flex-1">{publicUrl}</span>
          <Copy size={16} className="text-zinc-500 shrink-0" weight="duotone" />
        </button>

        {!data.subscription_active && (
          <div className="mt-4 bg-red-500/10 border border-red-500/40 rounded-xl p-4" data-testid="trial-expired-banner">
            <p className="text-sm font-bold text-red-400">{t("trial_expired_title")}</p>
            <p className="text-xs text-zinc-400 mt-1">{t("trial_expired_text")}</p>
          </div>
        )}

        <nav className="mt-6 flex gap-2 overflow-x-auto no-scrollbar" data-testid="panel-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={`tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`shrink-0 px-4 h-10 rounded-lg text-sm font-semibold transition-colors ${
                tab === tb.key ? "bg-amber-500 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </nav>

        <div className="mt-6">
          {tab === "agenda" && <AgendaTab country={data.country} />}
          {tab === "servicos" && <ServicesTab barber={data.barber} onImported={load} />}
          {tab === "perfil" && <ProfileTab barber={data.barber} onSaved={load} />}
          {tab === "assinatura" && <BillingTab info={data} onChanged={load} />}
          {tab === "ferramentas" && <ToolsTab country={data.country} />}
        </div>
      </div>
    </div>
  );
}
